import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  AnchoredDetailCallout,
  type AnchoredDetailCalloutProps,
} from "./AnchoredDetailCallout";

/** Inner shell for form-style callouts: fixed header/body/footer, no outer scroll. */
export const FORM_ANCHORED_DETAIL_CALLOUT_SHELL_CLASS =
  "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden";

/** Default width for compact confirm-or-edit callouts (batch hold, reserve editor, etc.). */
export const DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX = 320;

/** Minimum layout height while measuring so footers are not clipped against the border. */
export const DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX = 220;

export type FormAnchoredDetailCalloutProps = Omit<
  AnchoredDetailCalloutProps,
  "scrollMode" | "contentClassName"
> & {
  contentClassName?: string;
};

/**
 * Anchored callout preset for short forms: title, fields, and a footer action row.
 * Uses {@link AnchoredDetailCallout} with `scrollMode="none"` and a column shell.
 */
export function FormAnchoredDetailCallout({
  contentClassName,
  ...props
}: FormAnchoredDetailCalloutProps): JSX.Element | null {
  return (
    <AnchoredDetailCallout
      scrollMode="none"
      contentClassName={cn(FORM_ANCHORED_DETAIL_CALLOUT_SHELL_CLASS, contentClassName)}
      {...props}
    />
  );
}

export type AnchoredDetailCalloutBodyProps = {
  children: ReactNode;
  className?: string;
};

/** Padded main block above the footer (`p-4 pb-3`, vertical `space-y-3`). */
export function AnchoredDetailCalloutBody({
  children,
  className,
}: AnchoredDetailCalloutBodyProps): JSX.Element {
  return <div className={cn("space-y-3 p-4 pb-3", className)}>{children}</div>;
}

export type AnchoredDetailCalloutTitleProps = {
  children: ReactNode;
  className?: string;
};

/** Primary callout heading — sentence case title including entity id when relevant. */
export function AnchoredDetailCalloutTitle({
  children,
  className,
}: AnchoredDetailCalloutTitleProps): JSX.Element {
  return <h3 className={cn("text-sm font-semibold text-slate-900", className)}>{children}</h3>;
}

export type AnchoredDetailCalloutScrollRegionProps = {
  children: ReactNode;
  className?: string;
};

/** Scrollable middle section between header body and footer (tall forms). */
export function AnchoredDetailCalloutScrollRegion({
  children,
  className,
}: AnchoredDetailCalloutScrollRegionProps): JSX.Element {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-4 pb-3", className)}>{children}</div>;
}

export type AnchoredDetailCalloutFooterProps = {
  children: ReactNode;
  className?: string;
};

/** Footer action row: top border, `px-4 py-3`, right-aligned buttons. */
export function AnchoredDetailCalloutFooter({
  children,
  className,
}: AnchoredDetailCalloutFooterProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 bg-white px-4 py-3",
        className,
      )}
    >
      {children}
    </div>
  );
}

export type AnchoredDetailCalloutFieldLabelProps = {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
};

/** Standard label + control stack inside form callouts. */
export function AnchoredDetailCalloutFieldLabel({
  label,
  htmlFor,
  children,
  className,
}: AnchoredDetailCalloutFieldLabelProps): JSX.Element {
  return (
    <label htmlFor={htmlFor} className={cn("block text-xs font-medium text-slate-600", className)}>
      {label}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
